import get from 'lodash/get';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import Loader from '../../../../atoms/icons/loader';
import { FileCompareIcon } from '../../../../atoms/material-icons';
import LineGraph from '../../../../chart-templates/line_graph/line_graph';
import { fetchAbsolutePath, fetchStockId } from '../../utils/marketsUtils';
import EmptyState from '../empty-state';
import { useSelector } from 'react-redux';
import { isWithinTradingHours } from '../../../../../helpers/utils';
import './compare-price.m.css';

const ComparePrice = ({ currentStock }) => {
  const [movementData, setMovementData] = useState({});
  const [bseData, setBseData] = useState({});
  const [sensexdata, setSensexData] = useState({});
  const [indianIndicesData, setIndianIndicesData] = useState([]);
  const [stockId, setStockId] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState('');
  const stockGraphData = [];
  const stockLabelsData = [];
  const bseGraphData = [];
  const bseLabelsData = [];
  const sensexGraphData = [];
  const sensexLabelData = [];
  const allGraphData = [];
  const [initialLoading, setInitialLoading] = useState(true);
  const tradingHolidays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'trading_holidays'], []),
  );
  const specialTradingDays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'special_trading_days'], {}),
  );

  const getCompareMovementDataForQuery = async (stockId, duration = '1D') => {
    const response = await fetch(`/next/feapi/stock/movement/${stockId}/${duration}`);
    return response.json();
  };

  const getBseDataForQuery = async (duration = '1D') => {
    const response = await fetch(`/feapi/markets/indices/4/details/${duration}`);
    return response.json();
  };

  const getSensexDataForQuery = async (duration = '1D') => {
    const response = await fetch(`/feapi/markets/indices/1/details/${duration}`);
    return response.json();
  };

  const getIndianIndicesDataForQuery = async (duration = '1D') => {
    const response = await fetch(`/feapi/markets/indices/indian-indices/${duration}`);
    return response.json();
  };

  const resultCompareMovementData = useQuery(
    'getCompareMovementData',
    () => getCompareMovementDataForQuery(stockId, selectedDuration),
    {
      refetchInterval: 30000,
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        setMovementData(data.data);
      },
      enabled:
        initialLoading ||
        (!!stockId &&
          !!selectedDuration &&
          isWithinTradingHours(tradingHolidays, specialTradingDays)),
    },
  );

  const resultBseData = useQuery('getBseData', () => getBseDataForQuery(selectedDuration), {
    refetchInterval: 30000,
    refetchIntervalInBackground: true,
    onSuccess: (data) => {
      setBseData(data.data);
    },
    enabled:
      initialLoading ||
      (!!selectedDuration && isWithinTradingHours(tradingHolidays, specialTradingDays)),
  });

  const resultSensexData = useQuery(
    'getSensexData',
    () => getSensexDataForQuery(selectedDuration),
    {
      refetchInterval: 30000,
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        setSensexData(data.data);
      },
      enabled:
        initialLoading ||
        (!!selectedDuration && isWithinTradingHours(tradingHolidays, specialTradingDays)),
    },
  );

  const resultIndianIndicesCompareData = useQuery(
    'getIndianIndicesCompareData',
    () => getIndianIndicesDataForQuery(selectedDuration),
    {
      refetchInterval: 30000,
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        setIndianIndicesData(data.data);
      },
      enabled:
        initialLoading ||
        (!!selectedDuration && isWithinTradingHours(tradingHolidays, specialTradingDays)),
    },
  );

  const getMovementData = async (stockId, duration) => {
    setLoading(true);
    const response = await fetch(`/next/feapi/stock/movement/${stockId}/${duration}`);
    const data = await response.json();
    setMovementData(data?.data);
  };

  const getBseData = async (duration) => {
    setLoading(true);
    const response = await fetch(`/feapi/markets/indices/4/details/${duration}`);
    const data = await response.json();
    setBseData(data?.data);
  };

  const getSensexData = async (duration) => {
    setLoading(true);
    const response = await fetch(`/feapi/markets/indices/1/details/${duration}`);
    const data = await response.json();
    setSensexData(data?.data);
  };

  const getIndianIndicesData = async (duration) => {
    setLoading(true);
    const response = await fetch(`/feapi/markets/indices/indian-indices/${duration}`);
    const data = await response.json();
    setIndianIndicesData(data?.data);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const stockIdRetrieved = fetchStockId();
    const pagePath = fetchAbsolutePath();
    setLoading(true);
    setStockId(stockIdRetrieved);
    getMovementData(stockIdRetrieved, '1D');
    getBseData('1D');
    getSensexData('1D');
    getIndianIndicesData('1D');
    setLoading(false);
  }, []);

  useEffect(() => {
    setSelectedDuration('1D');
    if (resultCompareMovementData?.isSuccess) {
      setMovementData(resultCompareMovementData?.data?.data);
    }

    if (resultBseData?.isSuccess) {
      setBseData(resultBseData?.data?.data);
    }

    if (resultSensexData?.isSuccess) {
      setSensexData(resultSensexData?.data?.data);
    }

    if (resultIndianIndicesCompareData?.isSuccess) {
      setIndianIndicesData(resultIndianIndicesCompareData?.data?.data);
    }
  }, []);

  movementData?.graphDetails?.graph?.map((detail) => {
    stockGraphData.push(
      isNaN((detail[1] / movementData?.graphDetails?.graph[0][1]) * 100)
        ? 0
        : (detail[1] / movementData?.graphDetails?.graph[0][1]) * 100,
    );

    allGraphData.push({
      value: detail[1],
      calcValue: isNaN((detail[1] / movementData?.graphDetails?.graph[0][1]) * 100)
        ? 0
        : (detail[1] / movementData?.graphDetails?.graph[0][1]) * 100,
    });
    stockLabelsData.push(detail[0]);
  });

  bseData?.graph?.map((detail) => {
    bseGraphData.push(
      isNaN((detail[1] / bseData?.graph[0][1]) * 100)
        ? 0
        : (detail[1] / bseData?.graph[0][1]) * 100,
    );
    allGraphData.push({
      value: detail[1],
      calcValue: isNaN((detail[1] / bseData?.graph[0][1]) * 100)
        ? 0
        : (detail[1] / bseData?.graph[0][1]) * 100,
    });
    bseLabelsData.push(detail[0]);
  });

  sensexdata?.graph?.map((detail) => {
    sensexGraphData.push(
      isNaN((detail[1] / sensexdata?.graph[0][1]) * 100)
        ? 0
        : (detail[1] / sensexdata?.graph[0][1]) * 100,
    );
    allGraphData.push({
      value: detail[1],
      calcValue: isNaN((detail[1] / sensexdata?.graph[0][1]) * 100)
        ? 0
        : (detail[1] / sensexdata?.graph[0][1]) * 100,
    });
    sensexLabelData.push(detail[0]);
  });

  const seriesData = [
    {
      name: currentStock?.COMPNAME,
      color: '#0068D7',
      data: movementData?.graphDetails?.graph?.map((i) => ({
        name: i[0],
        y: (i[1] / movementData?.graphDetails?.graph[0][1]) * 100,
        value: i[1],
      })),
      value: movementData?.lowHigh?.currentPrice,
      chgp: movementData?.stockPercentage?.value,
    },
    {
      name: 'SENSEX',
      color: '#14c588',
      data: sensexdata?.graph?.map((i) => ({
        name: i[0],
        y: (i[1] / sensexdata?.graph[0][1]) * 100,
        value: i[1],
      })),
      value: get(sensexdata, ['price-movement', 'current-price'], ''),
      chgp: get(sensexdata, ['price-movement', 'chgp'], ''),
    },
    {
      name: 'BSE500',
      color: '#d01e51',
      data: bseData?.graph?.map((i) => ({
        name: i[0],
        y: (i[1] / bseData?.graph[0][1]) * 100,
        value: i[1],
      })),
      value: get(bseData, ['price-movement', 'current-price'], ''),
      chgp: get(bseData, ['price-movement', 'chgp'], ''),
    },
  ];

  const durationHandler = async (timePeriod) => {
    setLoading(true);
    getMovementData(stockId, timePeriod);
    getBseData(timePeriod);
    getSensexData(timePeriod);
    getIndianIndicesData(timePeriod);
    setTimeout(() => {
      setLoading(false);
    }, 8000);
  };

  const durationObj = ['1D', '1W', '1M', 'YTD', '1Y', '3Y', '5Y', '10Y'];

  if (initialLoading) {
    return <div styleName="initial-loading-container"></div>;
  }

  return (
    <div styleName="compare-price-container">
      <div styleName="header-title-container">
        <FileCompareIcon size={'26px'} color={'#d01e51'} />
        <h2 styleName="list-header-title">COMPARE PRICE</h2>
      </div>
      <div styleName="btn-container">
        {durationObj.map((val) => {
          return (
            <button
              key={val}
              styleName={`filter-btn ${val === selectedDuration ? 'selected' : ''}`}
              onClick={() => {
                durationHandler(val);
                setSelectedDuration(val);
              }}
            >
              {val}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div styleName="loader-container">
          <Loader color={'#dadee1'} />
        </div>
      ) : (
        <div styleName="graph-container">
          {stockGraphData?.length > 0 ||
            sensexGraphData?.length > 0 ||
            (bseGraphData > 0 && !loading) ? (
            <LineGraph
              containerId="compare-price-line-graph"
              seriesData={seriesData}
              height={200}
            />
          ) : (
            <div styleName="no-data-container-ext">
              <div styleName="no-data-container-int">
                <EmptyState subTextMessage={'for this stock at the moment'} />
              </div>
            </div>
          )}
        </div>
      )}

      <div styleName="fundamentals-body">
        <table>
          <tr>
            <td>
              <span styleName="dot-one"></span> {currentStock?.COMPNAME}
            </td>
            <td styleName="current-price">{movementData?.yearLowHigh?.currentPrice}</td>
            <td
              styleName={
                movementData?.stockPercentage?.value < 0
                  ? 'change-percentage-negative'
                  : 'change-percentage-positive'
              }
            >
              {movementData?.stockPercentage?.value
                ? `${movementData?.stockPercentage?.value}%`
                : '-'}
            </td>
          </tr>
          <tr>
            <td>
              <span styleName="dot-two"></span> {indianIndicesData[0]?.name}
            </td>
            <td styleName="current-price">
              {indianIndicesData[0]?.['price-movement']?.['current-price']}
            </td>
            <td
              styleName={
                indianIndicesData[0]?.['price-movement']?.['chgp'] < 0
                  ? 'change-percentage-negative'
                  : 'change-percentage-positive'
              }
            >
              {indianIndicesData[0]?.['price-movement']?.['chgp']
                ? `${indianIndicesData[0]?.['price-movement']?.['chgp']}%`
                : '-'}
            </td>
          </tr>
          <tr>
            <td>
              <span styleName="dot-three"></span> {indianIndicesData[5]?.name}
            </td>
            <td styleName="current-price">
              {indianIndicesData[5]?.['price-movement']?.['current-price']}
            </td>
            <td
              styleName={
                indianIndicesData[5]?.['price-movement']?.['chgp'] < 0
                  ? 'change-percentage-negative'
                  : 'change-percentage-positive'
              }
            >
              {indianIndicesData[5]?.['price-movement']?.['chgp']
                ? `${indianIndicesData[5]?.['price-movement']?.['chgp']}%`
                : '-'}
            </td>
          </tr>
        </table>
      </div>
    </div>
  );
};

ComparePrice.propTypes = {
  currentStock: PropTypes.object,
};

export default ComparePrice;
